import { Component, ChangeDetectionStrategy, ViewChild, computed, effect, inject, output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AiTelemetry } from "../../services/ai.telemetry";
import { AiAdvancedSearchResponse } from "./s25.chatbot.service";
import { S25ChatbotComponent } from "./s25.chat.bot.component";
import { SidebarStore } from "../s25-sidebar/s25.sidebar.store";

@TypeManagerDecorator("s25-ng-ai-search-chatbot")
@Component({
    selector: "s25-ng-ai-search-chatbot",
    template: `
        <s25-ng-ai-chatbot
            #chatbot
            [welcomeMessage]="
                'Provide details on what you are looking for and I can help you get started on a SeriesQL query.'
            "
            [mode]="'sidebar'"
            [title]="'AI Query Chat'"
            [windowMaxHeight]="'50vh'"
            [serviceConfig]="'advancedSearch'"
            [aiSession]="aiSession()"
            (responseReceived)="onResponseReceived($event)"
        />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25ChatbotComponent],
    standalone: true,
})
export class S25AiSearchChatbotComponent {
    @ViewChild("chatbot") chatbot!: S25ChatbotComponent<"advancedSearch">;

    readonly sidebarStore = inject(SidebarStore);

    readonly searchQuery = output<string>();

    readonly aiSession = computed(() => (this.sidebarStore.isOpen() ? AiTelemetry.createSession("Search") : undefined));

    constructor() {
        effect((onCleanup) => {
            const session = this.aiSession();
            if (!session) return;

            session.open();
            onCleanup(() => session.close());
        });
    }

    replacePlaceholders(
        response: AiAdvancedSearchResponse,
        confidenceThreshold: number = 0.6,
    ): { cleanedQuery: string; hasPlaceholders: boolean } {
        let hasPlaceholders = false;

        // Replace placeholders in the query with matching values if confidence is high enough
        const cleanedQuery = response.queryGrouping.replace(/\((\w+)\)/g, (_: string, term: string) => {
            const termMatch = response.matchingResponse?.termMatches.find((tm) => tm.term === term);

            if (termMatch && termMatch.confidenceNumber >= confidenceThreshold) {
                const matchedNames = termMatch.matches
                    .map((match) => `"${match.itemName} [ID:${match.itemId}]"`)
                    .join(", ");
                // "Student Housing [ID:-10000]"
                return `(${matchedNames})`;
            } else {
                hasPlaceholders = true;
                return "(PLACEHOLDER)";
            }
        });

        return { cleanedQuery, hasPlaceholders };
    }

    onResponseReceived(response: AiAdvancedSearchResponse) {
        const { cleanedQuery, hasPlaceholders } = this.replacePlaceholders(response, 0.5);

        this.searchQuery.emit("::" + cleanedQuery);
        this.aiSession()?.complete();

        let message = `I've generated the following query:\n${cleanedQuery}`;
        if (hasPlaceholders) {
            message += `\n\nPlease fill out the placeholder values to complete your query.`;
        }

        this.chatbot.addResponse(message);
    }
}
